import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { AuthContext } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { usePermission } from '../../hooks/usePermission';
import { Icon } from '../../ui';
import ChatWidget from '../ChatWidget';
import NotificationBell from '../NotificationBell';
import CyberBulletinMenu from './CyberBulletinMenu';
import NavigationMenu from './NavigationMenu';
import { LANG_OPTIONS } from '../Topbar';

import '../../pages/dashboard2/dashboard2.css';

/* Shared shell for the "Exposure Command" theme (console rail + tab nav +
   footer + chat). Mounted once per route group via <Outlet/>; pages render
   ONLY their own content (pagehead + body) and never repeat any of this. */

// permission: null → always visible (matches Topbar's NAV_DEFS convention)
const TABS = [
  { key: 'overview',   to: '/dashboard',        icon: 'dashboard', labelKey: 'dashboard2.overviewTab', permission: null },
  { key: 'assets',     to: '/assets',            icon: 'assets',    labelKey: 'nav.assets',            permission: 'asset:list' },
  { key: 'alerts', to: '/alerts', icon: 'alert', labelKey: 'Alerts', sections: [
    { key: 'all', to: '/alerts', label: 'All Alerts', permission: 'intelligence:query' },
    { key: 'settings', to: '/alerts/settings', label: 'Alert Settings', permission: 'notification:manage' },
  ] },
  { key: 'scans',      to: '/scans',             icon: 'scans',     labelKey: 'nav.scans',              permission: 'scan:list' },
  { key: 'rules', to: '/rule-manager', icon: 'rules', labelKey: 'Rule Policies', permission: 'whitelist:list' },
  { key: 'cyber-bulletin', to: '/cyber-bulletin', icon: 'report', labelKey: 'nav.cyberBulletin', permission: null },
  { key: 'reports', to: '/reports', icon: 'report', labelKey: 'dashboard2.reportsTab', sections: [
    { key: 'all', to: '/reports', label: 'All Reports', permission: 'notification:manage' },
    { key: 'settings', to: '/reports/settings', label: 'Report Settings', permission: 'notification:manage' },
  ] },
];

// Rail-search page registry: label comes from the live locale via labelKey,
// keywords cover en/tr synonyms the label itself doesn't contain.
// Permission-gated with the same can() convention as TABS.
const PAGE_DEFS = [
  { to: '/dashboard',       icon: 'dashboard',    labelKey: 'dashboard2.overviewTab',  permission: null,            keywords: ['dashboard', 'overview', 'panel'] },
  { to: '/assets',          icon: 'assets',       labelKey: 'nav.assets',              permission: 'asset:list',    keywords: ['asset', 'assets', 'varlik', 'varlıklar'] },
  { to: '/alerts', icon: 'alert', labelKey: 'Alerts', permission: 'intelligence:query', keywords: ['alerts', 'uyarı', 'bulgu'] },
  { to: '/scans',           icon: 'scans',        labelKey: 'nav.scans',               permission: 'scan:list',     keywords: ['scan', 'scans', 'tarama', 'taramalar'] },
  { to: '/cyber-bulletin',  icon: 'report',       labelKey: 'nav.cyberBulletin',       permission: null,            keywords: ['cyber bulletin', 'siber bulten', 'siber bülten', 'breach bulletin'] },
  { to: '/reports', icon: 'report', labelKey: 'dashboard2.reportsTab', permission: 'notification:manage', keywords: ['report', 'reports', 'rapor'] },
  { to: '/reports/settings', icon: 'settings', labelKey: 'Report Settings', permission: 'notification:manage', keywords: ['schedule', 'report settings'] },
  { to: '/alerts/settings', icon: 'settings', labelKey: 'Alert Settings', permission: 'notification:manage', keywords: ['notification', 'alert settings'] },
  { to: '/rule-manager', icon: 'rules', labelKey: 'Rule Policies', permission: 'whitelist:list', keywords: ['rules', 'whitelist', 'kural'] },
  { to: '/my-organization', icon: 'apiKeys',      labelKey: 'orgManagement.tabs.api',  permission: 'token:manage',  keywords: ['api', 'token', 'anahtar'], state: { activeTab: 'api' } },
  { to: '/my-organization', icon: 'organization', labelKey: 'nav.myOrg',               permission: null,            keywords: ['organization', 'org', 'organizasyon', 'kurum'] },
  { to: '/settings',        icon: 'settings',     labelKey: 'nav.settings',            permission: null,            keywords: ['settings', 'ayar', 'ayarlar', 'profile', 'profil'] },
  { to: '/users',           icon: 'users',        labelKey: 'nav.users',               permission: 'user:list',     keywords: ['user', 'users', 'kullanici', 'kullanıcılar'] },
  { to: '/admin-panel',     icon: 'admin',        labelKey: 'nav.adminPanel',          permission: 'settings:edit', keywords: ['admin', 'yonetim', 'yönetim'] },
];

const initials = (name = '') =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';

export default function D2Shell() {
  const { t, i18n } = useTranslation();
  const { user, activeOrg, setActiveOrg, orgs, logout } = useContext(AuthContext);
  const { theme, toggleTheme } = useTheme();
  const { can } = usePermission();
  const navigate = useNavigate();
  const location = useLocation();

  const [openNav, setOpenNav] = useState(null);
  useEffect(() => { setOpenNav(null); }, [location]);

  const [allAssets,   setAllAssets]   = useState([]);
  const [query,       setQuery]       = useState('');
  const [searchOpen,  setSearchOpen]  = useState(false);
  const [orgOpen,     setOrgOpen]     = useState(false);
  const [langOpen,    setLangOpen]    = useState(false);
  const [userOpen,    setUserOpen]    = useState(false);
  const [mSearchOpen, setMSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const mSearchRef = useRef(null);
  const orgRef = useRef(null);
  const langRef = useRef(null);
  const userRef = useRef(null);

  // Org switcher: platform admin or multi-org member (same rule as Topbar);
  // activeOrg null = "all my organizations" mode
  const canSwitchOrg = user?.role === 'admin' || orgs.length > 1;

  useEffect(() => {
    if (!orgOpen && !langOpen && !userOpen) return;
    const onClick = (e) => {
      if (orgOpen && orgRef.current && !orgRef.current.contains(e.target)) setOrgOpen(false);
      if (langOpen && langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
      if (userOpen && userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') { setOrgOpen(false); setLangOpen(false); setUserOpen(false); } };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [orgOpen, langOpen, userOpen]);

  useEffect(() => {
    if (mSearchOpen) setTimeout(() => mSearchRef.current?.focus(), 50);
  }, [mSearchOpen]);

  const chooseOrg = (org) => {
    setActiveOrg(org);
    setOrgOpen(false);
  };

  // "/" focuses the rail search (matches the kbd hint on the input)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && !/^(input|textarea|select)$/i.test(e.target.tagName)) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Lightweight asset fetch — only for rail search suggestions + the Assets
  // tab count. Scans are deliberately NOT fetched here (pages that need scan
  // data fetch it themselves) to avoid a second /api/scans call on every d2 route.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!can('asset:list')) return;
      const params = (user?.role === 'admin' && activeOrg) ? { org_id: activeOrg.id } : {};
      const as = await api.get('/api/assets', { params }).catch(() => null);
      if (cancelled) return;
      setAllAssets(as?.data?.assets || []);
    })();
    return () => { cancelled = true; };
  }, [can, user, activeOrg]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allAssets
      .filter(a => (a.target || '').toLowerCase().includes(q) || (a.name || '').toLowerCase().includes(q))
      .slice(0, 6);
  }, [query, allAssets]);

  const pageMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return PAGE_DEFS
      .filter(p => !p.permission || can(p.permission))
      .filter(p => t(p.labelKey).toLowerCase().includes(q) || p.keywords.some(k => k.includes(q)))
      .slice(0, 5);
  }, [query, can, t]);

  // Deep-link into the assets table's global search (?q= → DataTable setSearch)
  const goSearch = (text) => {
    const q = String(text ?? query).trim();
    if (!q) return;
    setSearchOpen(false);
    setMSearchOpen(false);
    setQuery('');
    navigate(`/assets?q=${encodeURIComponent(q)}`);
  };

  const goPage = (p) => {
    setSearchOpen(false);
    setMSearchOpen(false);
    setQuery('');
    navigate(p.to, p.state ? { state: p.state } : undefined);
  };

  const goAsset = (asset) => {
    if (asset.scan_id && can('scan:view')) {
      goPage({ to: `/scans/${asset.scan_id}` });
    } else {
      goSearch(asset.target || asset.name);
    }
  };

  // One search box, two mounts: rail (desktop) and the slide-down bar (mobile).
  // searchOpen stores WHICH mount owns the dropdown so the CSS-hidden twin
  // never renders a duplicate suggestion list.
  const renderSearchBox = (inputRef, withKbd, mountId) => (
    <div className="d2-search-wrap" onBlur={e => {
      if (!e.currentTarget.contains(e.relatedTarget)) setSearchOpen(false);
    }}>
      <div className="d2-search">
        <Icon name="search" size="sm" />
        <input
          ref={inputRef}
          value={query}
          onChange={e => { setQuery(e.target.value); setSearchOpen(mountId); }}
          onFocus={() => setSearchOpen(mountId)}
          onKeyDown={e => {
            if (e.key === 'Enter') goSearch();
            if (e.key === 'Escape') { setSearchOpen(false); setMSearchOpen(false); e.target.blur(); }
          }}
          placeholder={t('dashboard2.searchPlaceholder')} aria-label="Search"
        />
        {withKbd && <kbd>/</kbd>}
      </div>
      {searchOpen === mountId && (pageMatches.length > 0 || suggestions.length > 0) && (
        <div className="d2-search-pop" role="listbox">
          {pageMatches.map(p => (
            <button
              key={`${p.to}-${p.labelKey}`} className="d2-search-item" role="option" aria-selected={false}
              onMouseDown={e => e.preventDefault()}
              onClick={() => goPage(p)}
            >
              <Icon name={p.icon} size="sm" style={{ color: 'var(--d2-ink-3)', flexShrink: 0 }} />
              <span className="d2-search-item-target">{t(p.labelKey)}</span>
            </button>
          ))}
          {suggestions.map(a => (
            <button
              key={a.id} className="d2-search-item" role="option" aria-selected={false}
              onMouseDown={e => e.preventDefault()}
              onClick={() => goAsset(a)}
            >
              <Icon name="assets" size="sm" style={{ color: 'var(--d2-ink-3)', flexShrink: 0 }} />
              <span className="d2-search-item-target">{a.target || a.name}</span>
              {a.name && a.target && a.name !== a.target && (
                <span className="d2-search-item-sub">{a.name}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const currentLang = LANG_OPTIONS.find(l => l.code === (i18n.language || 'en')) || LANG_OPTIONS[0];

  const workspaceName = activeOrg?.name
    || (canSwitchOrg ? t('topbar.allOrganizations') : (user?.full_name || 'Workspace'));
  const userName = user?.full_name || user?.email || '';

  const isTabActive = (to) => location.pathname === to || location.pathname.startsWith(to + '/');

  return (
    <div className="d2">
      {/* ── Sticky top: rail + tabs size themselves; offsets never hard-coded ── */}
      <div className="d2-top">
      <header className="d2-rail">
        <img src="/logo-text.png" alt="StealerHunt" className="d2-rail-logo" />
        <div className="d2-org-wrap" ref={orgRef}>
          <button
            type="button" className="d2-org" title={workspaceName}
            disabled={!canSwitchOrg}
            aria-haspopup="listbox" aria-expanded={orgOpen}
            onClick={canSwitchOrg ? () => setOrgOpen(o => !o) : undefined}
          >
            <span className="d2-org-avatar">{initials(workspaceName)}</span>
            <span className="d2-org-name">{workspaceName}</span>
            {activeOrg?.plan && <span className="d2-org-plan">{activeOrg.plan}</span>}
            {canSwitchOrg && <Icon name="chevronDown" size="xs" style={{ color: 'var(--d2-rail-ink-2)' }} />}
          </button>
          {orgOpen && (
            <div className="d2-org-pop" role="listbox">
              <button
                className="d2-org-item" role="option" aria-selected={!activeOrg}
                onClick={() => chooseOrg(null)}
              >
                <span className="d2-org-avatar" style={{ background: 'var(--d2-inset)', color: 'var(--d2-ink-2)' }}>
                  <Icon name="layers" size="xs" />
                </span>
                <span className="d2-org-item-name">{t('topbar.allOrganizations')}</span>
                {!activeOrg && <Icon name="check" size="xs" style={{ marginLeft: 'auto', color: 'var(--d2-accent)', flexShrink: 0 }} />}
              </button>
              {orgs.length > 0 && <div className="d2-org-sep" />}
              {orgs.map(org => (
                <button
                  key={org.id} className="d2-org-item" role="option"
                  aria-selected={activeOrg?.id === org.id}
                  onClick={() => chooseOrg(org)}
                >
                  <span className="d2-org-avatar">{initials(org.name)}</span>
                  <span className="d2-org-item-name">{org.name}</span>
                  {org.plan && <span className="d2-org-plan">{org.plan}</span>}
                  {activeOrg?.id === org.id && <Icon name="check" size="xs" style={{ marginLeft: 'auto', color: 'var(--d2-accent)', flexShrink: 0 }} />}
                </button>
              ))}
            </div>
          )}
        </div>
        {renderSearchBox(searchRef, true, 'rail')}
        <div className="d2-rail-right">
          <button
            className="d2-railbtn d2-msearch-btn" aria-label="Search"
            aria-expanded={mSearchOpen}
            onClick={() => setMSearchOpen(o => !o)}
          >
            <Icon name="search" size="sm" />
          </button>
          <div className="d2-lang" ref={langRef}>
            <button
              type="button" className="d2-lang-btn"
              aria-haspopup="listbox" aria-expanded={langOpen} aria-label="Language"
              onClick={() => setLangOpen(o => !o)}
            >
              <span className="d2-lang-flag">{currentLang.flag}</span>
              <span>{currentLang.code.toUpperCase()}</span>
              <Icon name="chevronDown" size="xs" />
            </button>
            {langOpen && (
              <div className="d2-lang-pop" role="listbox">
                {LANG_OPTIONS.map(l => {
                  const selected = (i18n.language || 'en') === l.code;
                  return (
                    <button
                      key={l.code} type="button" role="option" aria-selected={selected}
                      className="d2-lang-item"
                      onClick={() => { i18n.changeLanguage(l.code); setLangOpen(false); }}
                    >
                      <span className="d2-lang-flag">{l.flag}</span>
                      <span className="d2-lang-item-name">{l.label}</span>
                      {selected && <Icon name="check" size="xs" style={{ marginLeft: 'auto', color: 'var(--d2-accent)', flexShrink: 0 }} />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <button className="d2-railbtn" onClick={toggleTheme} aria-label="Toggle theme">
            <Icon name={theme === 'dark' ? 'themeLight' : 'themeDark'} size="sm" />
          </button>
          <NotificationBell buttonClassName="d2-railbtn" />
          <button className="d2-railbtn d2-settings-btn" onClick={() => navigate('/settings')} aria-label={t('nav.settings')}>
            <Icon name="settings" size="sm" />
          </button>
          <div className="d2-user-wrap" ref={userRef}>
            <button
              type="button" className="d2-user"
              aria-haspopup="menu" aria-expanded={userOpen}
              onClick={() => setUserOpen(o => !o)}
            >
              <span className="d2-user-avatar">{initials(userName)}</span>
              <span className="d2-user-name">{userName}</span>
              <Icon name="chevronDown" size="xs" style={{ color: 'var(--d2-rail-ink-2)' }} />
            </button>
            {userOpen && (
              <div className="d2-user-pop" role="menu">
                <div className="d2-user-head">
                  <span className="d2-org-avatar">{initials(userName)}</span>
                  <span className="d2-user-head-text">
                    <span className="d2-user-head-name">{user?.full_name || user?.email?.split('@')[0]}</span>
                    <span className="d2-user-head-mail">{user?.email}</span>
                    <span className="d2-user-head-role">
                      {t(user?.role === 'org_admin' ? 'roles.orgAdmin' : `roles.${user?.role || 'viewer'}`).toUpperCase()}
                    </span>
                  </span>
                </div>
                <div className="d2-org-sep" />
                <button className="d2-org-item" role="menuitem" onClick={() => { setUserOpen(false); navigate('/settings'); }}>
                  <Icon name="settings" size="sm" /> {t('nav.settings')}
                </button>
                <button className="d2-org-item" role="menuitem" onClick={() => { setUserOpen(false); navigate('/my-organization'); }}>
                  <Icon name="organization" size="sm" /> {t('nav.myOrg')}
                </button>
                {can('user:list') && (
                  <button className="d2-org-item" role="menuitem" onClick={() => { setUserOpen(false); navigate('/users'); }}>
                    <Icon name="users" size="sm" /> {t('nav.users')}
                  </button>
                )}
                {can('settings:edit') && (
                  <button className="d2-org-item" role="menuitem" onClick={() => { setUserOpen(false); navigate('/admin-panel'); }}>
                    <Icon name="dashboard" size="sm" /> {t('nav.adminPanel')}
                  </button>
                )}
                <div className="d2-org-sep" />
                <button
                  className="d2-org-item d2-user-logout" role="menuitem"
                  onClick={() => { setUserOpen(false); logout(); navigate('/login'); }}
                >
                  <Icon name="logout" size="sm" /> {t('nav.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Mobile search bar (rail search is hidden ≤900px) ── */}
      {mSearchOpen && (
        <div className="d2-msearch">
          {renderSearchBox(mSearchRef, false, 'mobile')}
        </div>
      )}

      {/* ── Tab nav ── */}
      <nav className="d2-tabs">
        {TABS.filter(tab => (!tab.permission || can(tab.permission)) && (!tab.sections || tab.sections.some(section => can(section.permission)))).map(tab => {
          const active = isTabActive(tab.to);
          const count = tab.key === 'assets' ? allAssets.length : null;
          if (tab.key === 'cyber-bulletin') {
            return <CyberBulletinMenu key={tab.key} active={active} open={openNav === tab.key} onOpenChange={open => setOpenNav(open ? tab.key : null)} />;
          }
          if (tab.sections) {
            return <NavigationMenu key={tab.key} id={tab.key} label={t(tab.labelKey)} icon={tab.icon}
              active={active} open={openNav === tab.key} onOpenChange={open => setOpenNav(open ? tab.key : null)}
              sections={tab.sections.filter(section => can(section.permission)).map(section => ({ ...section, label: t(section.label) }))} />;
          }
          return (
            <Link key={tab.key} to={tab.to} className={`d2-tab${active ? ' on' : ''}`} aria-current={active ? 'page' : undefined}>
              <Icon name={tab.icon} size="sm" /> {t(tab.labelKey)}
              {count != null && <span className="d2-tab-count">{count}</span>}
            </Link>
          );
        })}
      </nav>
      </div>

      <main className="d2-body container-fluid">
        <Outlet />
      </main>

      {/* ── Footer — logo left, © year right, middle deliberately empty;
            pinned to viewport bottom by the flex column when content is short ── */}
      <footer className="d2-foot">
        <img
          src={theme === 'dark' ? '/logo-text.png' : '/logo-text-dark.png'}
          alt="StealerHunt" className="d2-foot-logo"
        />
        <span className="d2-foot-copy">© {new Date().getFullYear()}</span>
      </footer>

      <ChatWidget />
    </div>
  );
}
